// ===== World 7: Mole town – HOIST, PULLEY AND MINE CARTS =====
// Principle: a rope over a wheel – the heavier side goes down and lifts the other. Mine carts run on rails.
// Ball → goal: crystal ball → treasure chest.
import { defineWorld, definePart, defineObj } from '../../core/registry';
import type { Blocker, Ctx2D, GameBody, Gfx, IntroHelpers, ObjData, PhysicsApi, SkinDef, SoundFn, Vec, WorldDef, WorldMeta } from '../../core/types';
import { scene } from './scene';
import { levels } from './levels';
import { strings } from './strings';
import { K, DEG, type EndType, type HoistData, type HoistEnd } from './shared';

const INK = '#3B2A5C';
const MASS = { sack: 12, crystal: 5, leaf: 0.3 };
const BASE: Record<EndType, number> = { lift: 8, bucket: 3, cart: 6, gate: 10, sack: 12 };
const WR = 28;   // wheel radius
const ease = (k: number) => k < 0 ? 0 : k > 1 ? 1 : k * k * (3 - 2 * k);

// ---------- Types ----------
// A hoist has no single x/y; its level data is HoistData (shared.ts)
declare module '../../core/types' { interface ObjDataTypes { moles_hoist: HoistData } }
/** One local body of a rope end (relative to the end's pose) */
interface EndPart { x: number; y: number; w: number; h: number; a?: number; door?: boolean; front?: boolean }
interface Zone { x0: number; x1: number; y0: number; y1: number }
interface EndGeom { w: number; parts: EndPart[]; zone: Zone | null; hook: Vec; sz?: number }
interface Pose { x: number; y: number; a: number }
interface EndBody extends GameBody { lp: EndPart }
/** A rope end during play: bodies + how it moves with the rope position s */
interface End {
  e: HoistEnd; G: EndGeom; d: Vec; sgn: number; th: number; tag: string; type: EndType;
  base: number; load: number; bodies: EndBody[]; door: number; doorOpen: boolean; tipK: number; tipT: number; pose: Pose;
  /** How far the cart's wheels have rolled (drawing) */
  rollS?: number;
}
/** What the cart drawing needs (the intro animation builds one by hand) */
interface CartView {
  e: Pick<HoistEnd, 'x' | 'y' | 'tx' | 'ty' | 'tip' | 'tipA'>; G: { w: number }; d: Vec; sgn: number; th: number; tipK: number; pose: Pose; rollS?: number;
}
/** Rope system: one degree of freedom (s), like the kit's pulley */
interface HoistSys {
  o: HoistData; ends: End[]; wheels: Vec[]; s: number; v: number; min: number; max: number; damp: number; vmax: number;
  hold: boolean; held: boolean; heldT: number; rel: number; spin: number; moved: boolean; bodySet: Set<GameBody>;
  bump?: { t: number; v: number };
}
type HoistObj = ObjData & HoistData;
interface CartObj extends ObjData { tx: number; ty: number; tip?: boolean; hold?: boolean }
interface HoistSt { sys: HoistSys }

// ---------- Hoist geometry (local bodies for each end) ----------
function geom(e: HoistEnd): EndGeom {
  const t = e.type;
  if (t === 'lift') {
    const w = e.w || 120, ex = e.exit || 1;
    return { w, parts: [
      { x: 0, y: 0, w, h: 14, a: ex * 6 },
      { x: -ex * (w / 2 - 5), y: -(e.back || 64) / 2, w: 10, h: e.back || 64 },
      { x: ex * (w / 2 - 5), y: -32, w: 10, h: 64, door: true },
    ], zone: { x0: -w / 2 + 4, x1: w / 2 - 4, y0: -90, y1: 6 }, hook: { x: 0, y: -104 } };
  }
  if (t === 'bucket') {
    const w = e.w || 100;
    return { w, parts: [
      { x: 0, y: 0, w, h: 12 },
      { x: -w / 2 - 4, y: -26, w: 10, h: 58, a: -12 },
      { x: w / 2 + 4, y: -26, w: 10, h: 58, a: 12 },
    ], zone: { x0: -w / 2 - 10, x1: w / 2 + 10, y0: -95, y1: 6 }, hook: { x: 0, y: -86 } };
  }
  if (t === 'cart') {
    const w = e.w || 110;
    return { w, parts: [
      { x: 0, y: 0, w, h: 12 },
      { x: -w / 2 + 5, y: -22, w: 10, h: 44, front: e.tip && e.tx! < e.x },
      { x: w / 2 - 5, y: -22, w: 10, h: 44, front: e.tip && e.tx! >= e.x },
    ], zone: { x0: -w / 2 + 4, x1: w / 2 - 4, y0: -70, y1: 6 }, hook: e.hook || { x: (e.tx! >= e.x ? 1 : -1) * (e.w || 110) / 2, y: -16 } };
  }
  if (t === 'gate') {
    const h = e.h || 150;
    return { w: 26, parts: [{ x: 0, y: 0, w: 26, h }], zone: null, hook: { x: 0, y: -h / 2 } };
  }
  // 'sack': fixed counterweight (heavy sack of soil)
  const sz = 40 + (e.m || BASE.sack) * 1.6;
  return { w: sz, parts: [{ x: 0, y: -sz / 2, w: sz * 0.9, h: sz }], zone: null, hook: { x: 0, y: -sz - 10 }, sz };
}

// Rotate a local point
function rot(x: number, y: number, a: number): Vec { const c = Math.cos(a), s = Math.sin(a); return { x: x * c - y * s, y: x * s + y * c }; }

// One end of the rope: bodies + how it moves with s
function makeEnd(api: PhysicsApi, e: HoistEnd, tag: string): End {
  const G = geom(e);
  let d: Vec;
  if (e.type === 'cart') { const dx = e.tx! - e.x, dy = e.ty! - e.y, L = Math.hypot(dx, dy) || 1; d = { x: dx / L, y: dy / L }; }
  else d = { x: 0, y: tag === 'A' ? -1 : 1 };
  const sgn = e.type === 'cart' ? (e.tx! >= e.x ? 1 : -1) : 1;
  const th = e.type === 'cart' ? Math.atan2(d.y * sgn, d.x * sgn) : 0;
  const E: End = { e, G, d, sgn, th, tag, type: e.type, base: e.m != null ? e.m : BASE[e.type], load: 0, bodies: [], door: 0, doorOpen: false, tipK: 0, tipT: 0, pose: { x: e.x, y: e.y, a: th } };
  for (const p of G.parts) {
    const b = api.R(e.x, e.y, p.w, p.h, { isStatic: true, friction: 0.05, restitution: 0.1, chamfer: { radius: Math.min(5, p.h / 2 - 1, p.w / 2 - 1) } }) as EndBody;
    b.kind = K + '_' + e.type; b.lp = p;
    api.add(b); E.bodies.push(b);
  }
  return E;
}

function endPose(E: End, s: number): Pose {
  const e = E.e;
  const x = e.x + E.d.x * s, y = e.y + E.d.y * s;
  return { x, y, a: E.th };
}
// Put all bodies of an end in place
function placeEnd(api: PhysicsApi, E: End, s: number, init: boolean) {
  const P0 = endPose(E, s); E.pose = P0;
  const tip = E.tipK * (E.e.tipA || 55) * DEG * E.sgn;
  const piv = { x: E.sgn * E.G.w / 2, y: 8 };
  const pv = rot(piv.x, piv.y, P0.a);
  const Body = api.Body;
  for (const b of E.bodies) {
    const p = b.lp;
    const lx = p.x;
    let ly = p.y;
    if (p.door) ly = p.y - 72 * E.door;
    const q = rot(lx - piv.x, ly - piv.y, P0.a + tip);
    const pos = { x: P0.x + pv.x + q.x, y: P0.y + pv.y + q.y };
    const ang = P0.a + tip + (p.a || 0) * DEG;
    if (p.front) b.isSensor = E.tipK > 0.15;
    Body.setPosition(b, pos, !init);
    Body.setAngle(b, ang, !init);
  }
}
// Is the body inside the end's zone (local coordinates)?
function inZone(E: End, b: GameBody) {
  const z = E.G.zone; if (!z) return false;
  const P0 = E.pose, q = rot(b.position.x - P0.x, b.position.y - P0.y, -P0.a);
  return q.x > z.x0 && q.x < z.x1 && q.y > z.y0 && q.y < z.y1;
}

// ---------- Rope system (one degree of freedom, like the kit's pulley) ----------
function makeSystem(api: PhysicsApi, o: HoistData): HoistSys {
  const ends: End[] = [];
  if (o.A) ends.push(makeEnd(api, o.A, 'A'));
  if (o.B) ends.push(makeEnd(api, o.B, 'B'));
  const wheels = o.wheels || (o.wx != null ? [{ x: o.wx, y: o.wy! }] : []);
  const sys: HoistSys = { o, ends, wheels, s: 0, v: 0, min: o.min != null ? o.min : 0, max: o.max != null ? o.max : 200,
    damp: o.damp || 0.975, vmax: o.vmax || 4, hold: !!o.hold, held: !!o.hold, heldT: 0, rel: 0, spin: 0, moved: false, bodySet: new Set() };
  for (const E of ends) { for (const b of E.bodies) sys.bodySet.add(b); placeEnd(api, E, 0, true); }
  return sys;
}
function stepSystem(api: PhysicsApi, sys: HoistSys) {
  const S = api.S;
  // load in each end
  let heroIn = false;
  for (const E of sys.ends) {
    E.load = 0;
    if (!E.G.zone) continue;
    for (const b of S.dyn) {
      if (b.isStatic || sys.bodySet.has(b)) continue;
      if (inZone(E, b)) { E.load += b.mass; if (b.kind === 'hero') heroIn = true; }
    }
  }
  if (sys.held) {
    if (heroIn) sys.heldT++; else sys.heldT = 0;
    if (sys.heldT > 14) { sys.held = false; api.sfx('moles_click', sys.wheels[0] ? sys.wheels[0].x : sys.ends[0].e.x, sys.wheels[0] ? sys.wheels[0].y : sys.ends[0].e.y); }
  } else if (sys.rel < 1) sys.rel = Math.min(1, sys.rel + 0.08);
  if (!sys.held) {
    let F = 0, I = 0;
    for (const E of sys.ends) { const m = E.base + E.load; F += m * E.d.y; I += m; }
    const g = 0.2778 * api.engine.gravity.y;
    let a = g * F / Math.max(1, I);
    const fr = sys.o.fric || 0;
    if (fr) { if (Math.abs(sys.v) < 0.05) { if (Math.abs(a) <= fr) { a = 0; sys.v = 0; } else a -= Math.sign(a) * fr; } else a -= Math.sign(sys.v) * fr; }
    sys.v += a;
    sys.v *= sys.damp;
    if (Math.abs(sys.v) > sys.vmax) sys.v = Math.sign(sys.v) * sys.vmax;
    let ns = sys.s + sys.v;
    if (ns > sys.max) { ns = sys.max; if (sys.v > 1.2) sys.bump = { t: S.t, v: sys.v }; sys.v = 0; }
    if (ns < sys.min) { ns = sys.min; if (sys.v < -1.2) sys.bump = { t: S.t, v: sys.v }; sys.v = 0; }
    const dd = ns - sys.s;
    sys.s = ns; sys.spin += dd;
    if (Math.abs(sys.s) > 20) sys.moved = true;
    if (Math.abs(dd) > 0.3 && S.t % 8 === 0) api.emit('creak', sys.wheels[0] ? sys.wheels[0].x : sys.ends[0].pose.x, sys.wheels[0] ? sys.wheels[0].y : sys.ends[0].pose.y);
    if (sys.bump && sys.bump.t === S.t) api.sfx('moles_bump', sys.ends[0].pose.x, sys.ends[0].pose.y, '#C9A27A', 6);
  }
  const atEnd = sys.s >= sys.max - 0.01 || sys.s <= sys.min + 0.01;
  for (const E of sys.ends) {
    // the lift's door opens once the lift has gone all the way up/down
    if (E.type === 'lift' && !E.doorOpen && sys.moved && atEnd && Math.abs(sys.v) < 0.5) { E.doorOpen = true; api.sfx('moles_clonk', E.pose.x, E.pose.y - 40, '#FFE07A', 6); }
    if (E.doorOpen && E.door < 1) E.door = Math.min(1, E.door + 0.06);
    // the mine cart tips over at the end of the track
    if (E.type === 'cart' && E.e.tip) {
      const end = sys.s >= sys.max - 0.01 && sys.moved;
      if (end) E.tipT++;
      const up = E.e.tipBack === false ? 1e9 : 150;
      const k = E.tipT <= 0 ? 0 : E.tipT < 30 ? E.tipT / 30 : E.tipT < up ? 1 : Math.max(0, 1 - (E.tipT - up) / 30);
      if (E.tipT === 1) api.sfx('moles_tip', E.pose.x, E.pose.y, '#FFE07A', 8);
      E.tipK = ease(k);
    }
    placeEnd(api, E, sys.s, false);
  }
}
function busySystem(sys: HoistSys) {
  if (Math.abs(sys.v) > 0.05) return true;
  for (const E of sys.ends) {
    if (E.door > 0 && E.door < 1) return true;
    if (E.type === 'cart' && E.e.tip && E.tipT > 0 && E.tipT < 185) return true;
  }
  return false;
}

// ---------- Drawing helpers ----------
function wood(g: Gfx, ctx: Ctx2D, x: number, y: number, w: number, h: number, a: number) {
  g.block(ctx, x, y, w, h, a, '#EDB577', '#B97A45', Math.min(7, h / 2));
}
function steel(g: Gfx, ctx: Ctx2D, x: number, y: number, w: number, h: number, a: number) { g.block(ctx, x, y, w, h, a, '#C3CAD8', '#7D879C', Math.min(5, h / 2)); }

// Tangent point from point P to wheel C (pick the side where the rope runs over the top)
function tangent(P: Vec, C: Vec, R: number, left: boolean) {
  const dx = P.x - C.x, dy = P.y - C.y, d = Math.max(R + 0.1, Math.hypot(dx, dy));
  const ph = Math.atan2(dy, dx), al = Math.acos(R / d);
  const c = [ph + al, ph - al];
  const sc = (a: number) => left ? Math.sin(a) + Math.cos(a) : Math.sin(a) - Math.cos(a);
  let a = sc(c[0]) < sc(c[1]) ? c[0] : c[1];
  a = Math.atan2(Math.sin(a), Math.cos(a));
  if (left && a > -Math.PI / 2) a -= Math.PI * 2;
  return a;
}
function hookPt(E: End): Vec {
  const h = E.G.hook, P0 = E.pose, tip = E.tipK * (E.e.tipA || 55) * DEG * E.sgn;
  if (E.type === 'cart') {
    const piv = { x: E.sgn * E.G.w / 2, y: 8 }, pv = rot(piv.x, piv.y, P0.a), q = rot(h.x - piv.x, h.y - piv.y, P0.a + tip);
    return { x: P0.x + pv.x + q.x, y: P0.y + pv.y + q.y };
  }
  return { x: P0.x + h.x, y: P0.y + h.y };
}
function drawRope(ctx: Ctx2D, sys: HoistSys) {
  if (!sys.wheels.length || sys.ends.length < 2) {
    if (sys.wheels.length && sys.ends.length === 1) {
      const E = sys.ends[0], W0 = sys.wheels[0], hp = hookPt(E);
      ctx.strokeStyle = '#8A5A2E'; ctx.lineWidth = 4; ctx.lineCap = 'round';
      ctx.beginPath(); ctx.moveTo(hp.x, hp.y); ctx.lineTo(W0.x, W0.y); ctx.stroke();
    }
    return;
  }
  let [E1, E2] = sys.ends;
  const h1 = hookPt(E1), h2 = hookPt(E2);
  if (h1.x > h2.x) { [E1, E2] = [E2, E1]; }
  const hl = hookPt(E1), hr = hookPt(E2);
  const ws = sys.wheels.slice().sort((a, b) => a.x - b.x);
  const wl = ws[0], wr = ws[ws.length - 1];
  const aL = tangent(hl, wl, WR, true), aR = tangent(hr, wr, WR, false);
  ctx.save();
  ctx.lineCap = 'round'; ctx.lineJoin = 'round';
  const path = () => {
    ctx.beginPath(); ctx.moveTo(hl.x, hl.y);
    if (ws.length === 1) ctx.arc(wl.x, wl.y, WR, aL, aR);
    else { ctx.arc(wl.x, wl.y, WR, aL, -Math.PI / 2); ctx.lineTo(wr.x, wr.y - WR); ctx.arc(wr.x, wr.y, WR, -Math.PI / 2, aR); }
    ctx.lineTo(hr.x, hr.y);
  };
  path(); ctx.strokeStyle = '#6B4424'; ctx.lineWidth = 7; ctx.stroke();
  path(); ctx.strokeStyle = '#D9A566'; ctx.lineWidth = 4; ctx.stroke();
  ctx.setLineDash([3, 6]); path(); ctx.strokeStyle = 'rgba(107,68,36,.6)'; ctx.lineWidth = 3; ctx.lineDashOffset = -sys.spin; ctx.stroke(); ctx.setLineDash([]);
  ctx.restore();
}
function drawWheel(ctx: Ctx2D, g: Gfx, W0: Vec, spin: number, sys: HoistSys | null) {
  const { x, y } = W0;
  ctx.save();
  // beam up to the ceiling
  ctx.fillStyle = '#8A5A2E'; g.rr(ctx, x - 7, y - 70, 14, 70, 5); ctx.fill();
  ctx.strokeStyle = INK; ctx.lineWidth = 2.5; ctx.stroke();
  g.shadowOn(ctx, 10, 5);
  ctx.beginPath(); ctx.arc(x, y, WR + 2, 0, 7); ctx.fillStyle = '#6B4424'; ctx.fill(); g.shadowOff(ctx);
  ctx.beginPath(); ctx.arc(x, y, WR - 2, 0, 7); ctx.fillStyle = g.lg(ctx, y - WR, y + WR, '#FFD36B', '#E8912A'); ctx.fill();
  ctx.lineWidth = 3; ctx.strokeStyle = INK; ctx.beginPath(); ctx.arc(x, y, WR + 2, 0, 7); ctx.stroke();
  ctx.translate(x, y); ctx.rotate(spin / WR);
  ctx.strokeStyle = 'rgba(120,60,10,.75)'; ctx.lineWidth = 4; ctx.lineCap = 'round';
  for (let i = 0; i < 3; i++) { ctx.rotate(Math.PI / 3); ctx.beginPath(); ctx.moveTo(-WR + 7, 0); ctx.lineTo(WR - 7, 0); ctx.stroke(); }
  ctx.rotate(-spin / WR);
  ctx.beginPath(); ctx.arc(0, 0, 7, 0, 7); ctx.fillStyle = '#fff'; ctx.fill(); ctx.lineWidth = 2.5; ctx.strokeStyle = INK; ctx.stroke();
  // brake (hold): red block that swings away once the ball is in the lift
  if (sys && sys.hold) {
    const k = sys.held ? 0 : ease(sys.rel);
    ctx.rotate(-0.5 - k * 1.0);
    ctx.fillStyle = '#FF5A5F'; g.rr(ctx, -9, -WR - 20, 18, 16, 5); ctx.fill(); ctx.lineWidth = 2.5; ctx.strokeStyle = INK; ctx.stroke();
  }
  ctx.restore();
}
function drawEndBack(ctx: Ctx2D, g: Gfx, E: End) {
  const P0 = E.pose, e = E.e;
  if (E.type === 'lift') {
    const w = E.G.w;
    ctx.save(); ctx.translate(P0.x, P0.y);
    // back wall (dark shaft)
    g.rr(ctx, -w / 2, -66, w, 66, 8); ctx.fillStyle = 'rgba(92,60,40,.55)'; ctx.fill();
    // bracket up to the hook
    if (e.coal) { ctx.fillStyle = '#7D879C'; g.rr(ctx, -40, -80, 80, 10, 4); ctx.fill(); ctx.strokeStyle = INK; ctx.lineWidth = 2; ctx.stroke(); }
    ctx.strokeStyle = '#7D879C'; ctx.lineWidth = 5; ctx.beginPath(); ctx.moveTo(-w / 2 + 6, -64); ctx.lineTo(0, -104); ctx.lineTo(w / 2 - 6, -64); ctx.stroke();
    ctx.strokeStyle = INK; ctx.lineWidth = 2; ctx.stroke();
    ctx.beginPath(); ctx.arc(0, -104, 6, 0, 7); ctx.fillStyle = '#FFC93C'; ctx.fill(); ctx.stroke();
    ctx.restore();
  } else if (E.type === 'bucket') {
    const w = E.G.w;
    ctx.save(); ctx.translate(P0.x, P0.y);
    ctx.beginPath(); ctx.moveTo(-w / 2 - 14, -54); ctx.lineTo(w / 2 + 14, -54); ctx.lineTo(w / 2, 6); ctx.lineTo(-w / 2, 6); ctx.closePath();
    ctx.fillStyle = '#6E7892'; ctx.fill();
    ctx.strokeStyle = '#7D879C'; ctx.lineWidth = 5; ctx.beginPath(); ctx.moveTo(-w / 2 - 12, -52); ctx.quadraticCurveTo(0, -120, w / 2 + 12, -52); ctx.stroke();
    ctx.strokeStyle = INK; ctx.lineWidth = 2; ctx.stroke();
    ctx.beginPath(); ctx.arc(0, -86, 5, 0, 7); ctx.fillStyle = '#FFC93C'; ctx.fill(); ctx.stroke();
    ctx.restore();
  } else if (E.type === 'cart') {
    drawTrack(ctx, g, E); drawCartBack(ctx, g, E);
  }
}
function drawTrack(ctx: Ctx2D, g: Gfx, E: CartView) {
  const e = E.e, w = E.G.w;
  const ox = rot(0, 22, E.th);
  const x0 = e.x - E.d.x * (w / 2 + 10) + ox.x, y0 = e.y - E.d.y * (w / 2 + 10) + ox.y;
  const x1 = e.tx! + E.d.x * (w / 2 + (e.tip ? -4 : 10)) + ox.x, y1 = e.ty! + E.d.y * (w / 2 + (e.tip ? -4 : 10)) + ox.y;
  ctx.save();
  // sleepers
  const L = Math.hypot(x1 - x0, y1 - y0), n = Math.max(2, Math.floor(L / 34));
  for (let i = 0; i <= n; i++) {
    const k = i / n, x = x0 + (x1 - x0) * k, y = y0 + (y1 - y0) * k;
    ctx.save(); ctx.translate(x, y); ctx.rotate(E.th);
    ctx.fillStyle = '#9A6538'; g.rr(ctx, -6, -3, 12, 14, 3); ctx.fill(); ctx.strokeStyle = INK; ctx.lineWidth = 2; ctx.stroke();
    ctx.restore();
  }
  ctx.lineCap = 'round';
  ctx.strokeStyle = INK; ctx.lineWidth = 8; ctx.beginPath(); ctx.moveTo(x0, y0); ctx.lineTo(x1, y1); ctx.stroke();
  ctx.strokeStyle = '#C3CAD8'; ctx.lineWidth = 4; ctx.beginPath(); ctx.moveTo(x0, y0); ctx.lineTo(x1, y1); ctx.stroke();
  // buffer stop
  if (e.tip) { const q = rot(8, -8, E.th); ctx.fillStyle = '#FF5A5F'; ctx.beginPath(); ctx.arc(x1 + q.x, y1 + q.y, 8, 0, 7); ctx.fill(); ctx.strokeStyle = INK; ctx.lineWidth = 2.5; ctx.stroke(); }
  ctx.restore();
}
function drawEndFront(ctx: Ctx2D, g: Gfx, E: End) {
  const P0 = E.pose, e = E.e;
  if (E.type === 'lift') {
    const w = E.G.w, ex = e.exit || 1;
    ctx.save(); ctx.translate(P0.x, P0.y);
    // floor
    wood(g, ctx, 0, 0, w, 14, ex * 6 * DEG);
    // bars at the sides
    const bk = e.back || 64;
    steel(g, ctx, -ex * (w / 2 - 5), -bk / 2, 10, bk, 0);
    steel(g, ctx, ex * (w / 2 - 5), -32 - 72 * E.door, 10, 64, 0);
    ctx.fillStyle = '#FFC93C'; g.rr(ctx, ex * (w / 2 - 5) - 7, -60 - 72 * E.door, 14, 10, 3); ctx.fill(); ctx.strokeStyle = INK; ctx.lineWidth = 2; ctx.stroke();
    // top frame (only when the lift is closed on both sides)
    if (!e.back) { ctx.fillStyle = '#7D879C'; g.rr(ctx, -w / 2, -70, w, 8, 4); ctx.fill(); ctx.strokeStyle = INK; ctx.lineWidth = 2; ctx.stroke(); }
    // extra load: coal on the roof
    if (e.coal) for (const [cx, cy] of [[-22, -84], [0, -90], [22, -84]]) coal(ctx, g, cx, cy + 6, cx * 0.02, false, 0.55);
    ctx.restore();
  } else if (E.type === 'bucket') {
    const w = E.G.w;
    ctx.save(); ctx.translate(P0.x, P0.y);
    g.shadowOn(ctx, 8, 4);
    ctx.beginPath(); ctx.moveTo(-w / 2 - 14, -30); ctx.lineTo(w / 2 + 14 - 5, -30); ctx.lineTo(w / 2 + 2, 7); ctx.lineTo(-w / 2 - 2, 7); ctx.closePath();
    ctx.fillStyle = g.lg(ctx, -30, 7, '#C3CAD8', '#7D879C'); ctx.fill(); g.shadowOff(ctx);
    ctx.lineWidth = 3; ctx.strokeStyle = INK; ctx.stroke();
    // rim + rivets
    ctx.beginPath(); ctx.moveTo(-w / 2 - 14, -54); ctx.lineTo(-w / 2 - 2, 7); ctx.moveTo(w / 2 + 14, -54); ctx.lineTo(w / 2 + 2, 7); ctx.lineWidth = 5; ctx.strokeStyle = '#7D879C'; ctx.stroke(); ctx.lineWidth = 2; ctx.strokeStyle = INK; ctx.stroke();
    ctx.fillStyle = 'rgba(255,255,255,.35)'; g.rr(ctx, -w / 2 - 6, -26, w + 6, 5, 2); ctx.fill();
    ctx.fillStyle = INK; for (const k of [-1, 0, 1]) { ctx.beginPath(); ctx.arc(k * w / 3, -12, 2.5, 0, 7); ctx.fill(); }
    ctx.restore();
  } else if (E.type === 'cart') {
    drawCart(ctx, g, E);
  } else if (E.type === 'gate') {
    const h = e.h || 150;
    ctx.save(); ctx.translate(P0.x, P0.y);
    wood(g, ctx, 0, 0, 26, h, 0);
    ctx.strokeStyle = '#7D879C'; ctx.lineWidth = 4;
    for (const k of [-1, 1]) { ctx.beginPath(); ctx.moveTo(-13, k * h / 3); ctx.lineTo(13, k * h / 3); ctx.stroke(); }
    ctx.restore();
  } else if (E.type === 'sack') {
    drawSack(ctx, g, P0.x, P0.y - E.G.sz! / 2, E.G.sz! / 52, 0, false);
  }
}
function cartXf(ctx: Ctx2D, E: CartView) {
  const w = E.G.w, P0 = E.pose, tip = E.tipK * (E.e.tipA || 55) * DEG * E.sgn;
  const piv = { x: E.sgn * w / 2, y: 8 }, pv = rot(piv.x, piv.y, P0.a);
  ctx.translate(P0.x + pv.x, P0.y + pv.y); ctx.rotate(P0.a + tip); ctx.translate(-piv.x, -piv.y);
}
function drawCartBack(ctx: Ctx2D, g: Gfx, E: CartView) {
  const w = E.G.w;
  ctx.save(); cartXf(ctx, E);
  ctx.beginPath(); ctx.moveTo(-w / 2, -44); ctx.lineTo(w / 2, -44); ctx.lineTo(w / 2 - 4, 4); ctx.lineTo(-w / 2 + 4, 4); ctx.closePath();
  ctx.fillStyle = '#56637E'; ctx.fill(); ctx.lineWidth = 3; ctx.strokeStyle = INK; ctx.stroke();
  ctx.fillStyle = '#7D879C'; g.rr(ctx, -w / 2 - 3, -48, w + 6, 8, 4); ctx.fill(); ctx.lineWidth = 2; ctx.stroke();
  ctx.restore();
}
function drawCart(ctx: Ctx2D, g: Gfx, E: CartView) {
  const w = E.G.w, P0 = E.pose, tip = E.tipK * (E.e.tipA || 55) * DEG * E.sgn;
  const piv = { x: E.sgn * w / 2, y: 8 }, pv = rot(piv.x, piv.y, P0.a);
  ctx.save();
  ctx.translate(P0.x + pv.x, P0.y + pv.y); ctx.rotate(P0.a + tip); ctx.translate(-piv.x, -piv.y);
  // wheels
  const rolled = E.rollS || 0;
  for (const k of [-1, 1]) {
    ctx.save(); ctx.translate(k * w * 0.3, 14);
    ctx.beginPath(); ctx.arc(0, 0, 11, 0, 7); ctx.fillStyle = '#4A4458'; ctx.fill(); ctx.lineWidth = 2.5; ctx.strokeStyle = INK; ctx.stroke();
    ctx.rotate(rolled / 11); ctx.strokeStyle = '#C3CAD8'; ctx.lineWidth = 3; ctx.beginPath(); ctx.moveTo(-7, 0); ctx.lineTo(7, 0); ctx.moveTo(0, -7); ctx.lineTo(0, 7); ctx.stroke();
    ctx.restore();
  }
  // body (a little lower at the front so the ball can be seen)
  g.shadowOn(ctx, 10, 5);
  ctx.beginPath(); ctx.moveTo(-w / 2 - 4, -16); ctx.lineTo(w / 2 + 4, -16); ctx.lineTo(w / 2 - 6, 8); ctx.lineTo(-w / 2 + 6, 8); ctx.closePath();
  ctx.fillStyle = g.lg(ctx, -16, 8, '#8FA3C0', '#56637E'); ctx.fill(); g.shadowOff(ctx);
  ctx.lineWidth = 3; ctx.strokeStyle = INK; ctx.stroke();
  ctx.fillStyle = '#FFC93C'; g.rr(ctx, -w / 2 - 6, -22, w + 12, 9, 4); ctx.fill(); ctx.lineWidth = 2.5; ctx.stroke();
  ctx.fillStyle = 'rgba(255,255,255,.3)'; g.rr(ctx, -w / 2 + 4, -11, w - 8, 4, 2); ctx.fill();
  ctx.fillStyle = INK; for (const k of [-1, 1]) for (const j of [-1, 1]) { ctx.beginPath(); ctx.arc(k * (w / 2 - 12), -2 + j * 5, 2.3, 0, 7); ctx.fill(); }
  ctx.restore();
}
// Sack of soil
function drawSack(ctx: Ctx2D, g: Gfx, x: number, y: number, s: number, a: number, sel: boolean) {
  ctx.save(); ctx.translate(x, y); ctx.rotate(a || 0); ctx.scale(s, s);
  g.shadowOn(ctx, 10, 5);
  ctx.beginPath();
  ctx.moveTo(-14, -22); ctx.quadraticCurveTo(-30, -6, -25, 16); ctx.quadraticCurveTo(-22, 27, 0, 27); ctx.quadraticCurveTo(22, 27, 25, 16); ctx.quadraticCurveTo(30, -6, 14, -22); ctx.closePath();
  ctx.fillStyle = g.lg(ctx, -24, 27, '#E8C48E', '#B48450'); ctx.fill(); g.shadowOff(ctx);
  ctx.lineWidth = 3; ctx.strokeStyle = INK; ctx.stroke();
  // soil at the top + string
  ctx.beginPath(); ctx.ellipse(0, -24, 11, 5, 0, 0, 7); ctx.fillStyle = '#7A4B2A'; ctx.fill(); ctx.stroke();
  ctx.strokeStyle = '#C0392B'; ctx.lineWidth = 3.5; ctx.beginPath(); ctx.moveTo(-15, -17); ctx.quadraticCurveTo(0, -12, 15, -17); ctx.stroke();
  // patch
  ctx.fillStyle = 'rgba(122,75,42,.35)'; g.rr(ctx, 4, 2, 13, 11, 3); ctx.fill();
  ctx.strokeStyle = 'rgba(59,42,92,.5)'; ctx.lineWidth = 1.5; ctx.setLineDash([2, 3]); g.rr(ctx, 4, 2, 13, 11, 3); ctx.stroke(); ctx.setLineDash([]);
  // "heavy" strokes
  ctx.strokeStyle = 'rgba(59,42,92,.35)'; ctx.lineWidth = 2.5; ctx.lineCap = 'round';
  ctx.beginPath(); ctx.moveTo(-14, 20); ctx.lineTo(-6, 22); ctx.stroke();
  if (sel) { ctx.strokeStyle = '#FFC93C'; ctx.lineWidth = 4; ctx.beginPath(); ctx.ellipse(0, 2, 34, 34, 0, 0, 7); ctx.stroke(); }
  ctx.restore();
}
function drawGem(ctx: Ctx2D, g: Gfx, x: number, y: number, a: number, sel: boolean, sc?: number) {
  ctx.save(); ctx.translate(x, y); ctx.rotate(a || 0); if (sc) ctx.scale(sc, sc);
  const pts = [[0, -21], [17, -9], [17, 11], [0, 21], [-17, 11], [-17, -9]];
  g.shadowOn(ctx, 10, 5);
  ctx.beginPath(); pts.forEach(([px, py]) => ctx.lineTo(px, py)); ctx.closePath();
  const gr = ctx.createLinearGradient(-17, -21, 17, 21); gr.addColorStop(0, '#B8F4FF'); gr.addColorStop(0.5, '#4FC9E8'); gr.addColorStop(1, '#2B7FC9');
  ctx.fillStyle = gr; ctx.fill(); g.shadowOff(ctx);
  ctx.lineWidth = 3; ctx.strokeStyle = INK; ctx.stroke();
  ctx.strokeStyle = 'rgba(255,255,255,.7)'; ctx.lineWidth = 2;
  ctx.beginPath(); ctx.moveTo(-17, -9); ctx.lineTo(0, -2); ctx.lineTo(17, -9); ctx.moveTo(0, -2); ctx.lineTo(0, 21); ctx.stroke();
  ctx.fillStyle = 'rgba(255,255,255,.8)'; ctx.beginPath(); ctx.ellipse(-8, -8, 4, 2.5, -0.6, 0, 7); ctx.fill();
  if (sel) { ctx.strokeStyle = '#FFC93C'; ctx.lineWidth = 4; ctx.beginPath(); ctx.arc(0, 0, 28, 0, 7); ctx.stroke(); }
  ctx.restore();
}
function drawLeaf(ctx: Ctx2D, g: Gfx, x: number, y: number, a: number, sel: boolean) {
  ctx.save(); ctx.translate(x, y); ctx.rotate(a || 0);
  g.shadowOn(ctx, 6, 3);
  ctx.beginPath(); ctx.moveTo(-26, 0); ctx.quadraticCurveTo(-4, -18, 24, -2); ctx.quadraticCurveTo(-2, 16, -26, 0); ctx.closePath();
  ctx.fillStyle = g.lg(ctx, -12, 12, '#B6EE7A', '#5DBB3C'); ctx.fill(); g.shadowOff(ctx);
  ctx.lineWidth = 2.5; ctx.strokeStyle = INK; ctx.stroke();
  ctx.strokeStyle = '#3E8E2C'; ctx.lineWidth = 2; ctx.beginPath(); ctx.moveTo(-28, 1); ctx.quadraticCurveTo(0, -1, 20, -2); ctx.stroke();
  ctx.beginPath(); ctx.moveTo(-8, -1); ctx.lineTo(-2, -8); ctx.moveTo(4, -1); ctx.lineTo(10, -7); ctx.moveTo(-8, 0); ctx.lineTo(-2, 6); ctx.stroke();
  if (sel) { ctx.strokeStyle = '#FFC93C'; ctx.lineWidth = 4; g.rr(ctx, -34, -16, 66, 30, 12); ctx.stroke(); }
  ctx.restore();
}

// Little mole with helmet and lamp (decoration)
function mole(ctx: Ctx2D, g: Gfx, x: number, y: number, s: number, t: number, look: number) {
  ctx.save(); ctx.translate(x, y); ctx.scale(s * (look || 1), s);
  const bob = Math.sin(t * 0.06) * 1.5;
  ctx.translate(0, bob);
  g.shadowOn(ctx, 8, 4);
  ctx.beginPath(); ctx.ellipse(0, 0, 22, 20, 0, 0, 7); ctx.fillStyle = '#6E5A7E'; ctx.fill(); g.shadowOff(ctx);
  ctx.lineWidth = 2.5; ctx.strokeStyle = INK; ctx.stroke();
  ctx.beginPath(); ctx.ellipse(10, 4, 9, 7, 0, 0, 7); ctx.fillStyle = '#FFB3C7'; ctx.fill(); ctx.stroke();
  ctx.beginPath(); ctx.arc(18, 3, 3.5, 0, 7); ctx.fillStyle = '#FF6F91'; ctx.fill();
  ctx.fillStyle = INK; ctx.beginPath(); ctx.arc(4, -5, 2.5, 0, 7); ctx.fill();
  ctx.beginPath(); ctx.arc(0, -14, 17, Math.PI, 0); ctx.closePath(); ctx.fillStyle = '#FFC93C'; ctx.fill(); ctx.stroke();
  ctx.fillStyle = '#FFF3A8'; ctx.beginPath(); ctx.arc(7, -24, 5, 0, 7); ctx.fill(); ctx.stroke();
  ctx.fillStyle = '#FFB3C7'; ctx.beginPath(); ctx.ellipse(-8, 18, 7, 4, 0, 0, 7); ctx.fill(); ctx.stroke(); ctx.beginPath(); ctx.ellipse(12, 18, 7, 4, 0, 0, 7); ctx.fill(); ctx.stroke();
  ctx.restore();
}

// Kit parts in the world's style
function railPlank(ctx: Ctx2D, g: Gfx, x: number, y: number, w: number, h: number, a: number, sel?: boolean) {
  // outline only checks truthiness (same as passing sel)
  g.block(ctx, x, y, w, h, a, '#E6A968', '#A8683A', 6, { outline: sel ? '#FFC93C' : null });
  ctx.save(); ctx.translate(x, y); ctx.rotate(a);
  ctx.fillStyle = 'rgba(90,50,20,.35)';
  for (let px = -w / 2 + 16; px < w / 2 - 8; px += 26) { g.rr(ctx, px - 3, -h / 2 + 3, 6, h - 4, 2); ctx.fill(); }
  ctx.strokeStyle = INK; ctx.lineWidth = 6; ctx.lineCap = 'round';
  ctx.beginPath(); ctx.moveTo(-w / 2 + 6, -h / 2 - 1); ctx.lineTo(w / 2 - 6, -h / 2 - 1); ctx.stroke();
  ctx.strokeStyle = '#D5DCE8'; ctx.lineWidth = 3;
  ctx.beginPath(); ctx.moveTo(-w / 2 + 6, -h / 2 - 1); ctx.lineTo(w / 2 - 6, -h / 2 - 1); ctx.stroke();
  ctx.restore();
}
function mushroom(ctx: Ctx2D, g: Gfx, x: number, y: number, a: number, w: number, sel?: boolean) {
  ctx.save(); ctx.translate(x, y); ctx.rotate(a);
  g.shadowOn(ctx, 8, 4);
  g.rr(ctx, -12, 0, 24, 26, 8); ctx.fillStyle = '#FFF1DC'; ctx.fill(); g.shadowOff(ctx);
  ctx.lineWidth = 2.5; ctx.strokeStyle = INK; ctx.stroke();
  ctx.beginPath(); ctx.moveTo(-w / 2, 6); ctx.quadraticCurveTo(-w / 2, -16, 0, -16); ctx.quadraticCurveTo(w / 2, -16, w / 2, 6); ctx.closePath();
  ctx.fillStyle = g.lg(ctx, -16, 6, '#FF8A8A', '#E0394A'); ctx.fill(); ctx.lineWidth = 3; ctx.stroke();
  ctx.fillStyle = '#fff';
  for (const [px, py, r] of [[-w / 4, -5, 5], [4, -10, 4], [w / 4 + 2, -3, 5]]) { ctx.beginPath(); ctx.arc(px, py, r, 0, 7); ctx.fill(); }
  if (sel) { ctx.strokeStyle = '#FFC93C'; ctx.lineWidth = 4; g.rr(ctx, -w / 2 - 5, -22, w + 10, 52, 12); ctx.stroke(); }
  ctx.restore();
}
function coal(ctx: Ctx2D, g: Gfx, x: number, y: number, a: number, sel?: boolean, sc?: number) {
  ctx.save(); ctx.translate(x, y); ctx.rotate(a); if (sc) ctx.scale(sc, sc);
  g.shadowOn(ctx, 10, 6);
  ctx.beginPath();
  for (let i = 0; i < 9; i++) { const t = i / 9 * Math.PI * 2, r = 24 + Math.sin(i * 2.3) * 2.5; ctx.lineTo(Math.cos(t) * r, Math.sin(t) * r); }
  ctx.closePath();
  const gr = ctx.createRadialGradient(-8, -10, 3, 0, 0, 28); gr.addColorStop(0, '#8C8699'); gr.addColorStop(1, '#3E3950');
  ctx.fillStyle = gr; ctx.fill(); g.shadowOff(ctx); ctx.lineWidth = 3; ctx.strokeStyle = INK; ctx.stroke();
  ctx.fillStyle = 'rgba(255,255,255,.5)'; ctx.beginPath(); ctx.moveTo(-10, -12); ctx.lineTo(-4, -16); ctx.lineTo(-2, -10); ctx.closePath(); ctx.fill();
  if (sel) { ctx.beginPath(); ctx.arc(0, 0, 30, 0, 7); ctx.strokeStyle = '#FFC93C'; ctx.lineWidth = 4; ctx.stroke(); }
  ctx.restore();
}
function mud(ctx: Ctx2D, g: Gfx, x: number, y: number, sel?: boolean) {
  ctx.save(); ctx.translate(x, y);
  g.shadowOn(ctx, 6, 3);
  ctx.beginPath(); ctx.ellipse(0, 8, 42, 18, 0, 0, 7); ctx.fillStyle = '#7A4B2A'; ctx.fill(); g.shadowOff(ctx);
  ctx.lineWidth = 3; ctx.strokeStyle = INK; ctx.stroke();
  ctx.fillStyle = '#9A6538'; ctx.beginPath(); ctx.ellipse(-6, 4, 26, 9, 0, 0, 7); ctx.fill();
  ctx.fillStyle = 'rgba(255,255,255,.35)'; for (const [px, py, r] of [[-16, 2, 4], [10, 10, 3], [20, 0, 2.5]]) { ctx.beginPath(); ctx.arc(px, py, r, 0, 7); ctx.fill(); }
  if (sel) { g.rr(ctx, -46, -16, 92, 46, 14); ctx.strokeStyle = '#FFC93C'; ctx.lineWidth = 4; ctx.setLineDash([8, 6]); ctx.stroke(); }
  ctx.restore();
}

// Backdrop on top of the theme picture: darker soil at the bottom and a few roots at the top
function bgExtra(ctx: Ctx2D, W: number, H: number) {
  const gr = ctx.createLinearGradient(0, H - 90, 0, H); gr.addColorStop(0, 'rgba(110,70,45,0)'); gr.addColorStop(1, 'rgba(110,70,45,.25)');
  ctx.fillStyle = gr; ctx.fillRect(0, H - 90, W, 90);
  ctx.strokeStyle = 'rgba(120,80,50,.35)'; ctx.lineWidth = 5; ctx.lineCap = 'round';
  for (const [x, l] of [[90, 40], [470, 30], [700, 46], [1010, 34]]) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.quadraticCurveTo(x + 14, l * 0.6, x - 4, l); ctx.stroke(); }
}

// ---------- Intro animations (? button) ----------
function introHoist(ctx: Ctx2D, t: number, g: Gfx, h: IntroHelpers, what: string) {
  // wheels at the top, the lift with the ball on the left, the bucket on the right; the sack drops into the bucket → the lift goes up
  const wx1 = 170, wx2 = 290, wy = 44;
  const drop = t < 50 ? 0 : Math.min(1, (t - 50) / 30);
  const lift = t < 80 ? 0 : h.ease((t - 80) / 90);
  const s = lift * 110;
  const lx = wx1, ly = 225 - s, bx = wx2, by = 115 + s;
  ctx.save();
  ctx.lineCap = 'round';
  ctx.strokeStyle = '#D9A566'; ctx.lineWidth = 4;
  ctx.beginPath(); ctx.moveTo(lx, ly - 90); ctx.lineTo(wx1 - WR, wy); ctx.arc(wx1, wy, WR, Math.PI, -Math.PI / 2); ctx.lineTo(wx2, wy - WR); ctx.arc(wx2, wy, WR, -Math.PI / 2, 0); ctx.lineTo(bx, by - 70); ctx.stroke();
  for (const x of [wx1, wx2]) drawWheel(ctx, g, { x, y: wy }, s, null);
  // lift
  ctx.fillStyle = 'rgba(92,60,40,.4)'; g.rr(ctx, lx - 50, ly - 60, 100, 60, 8); ctx.fill();
  g.ball(ctx, h.fakeBall(lx + 10, ly - 22, 0, 0, 15), 'hero', t);
  wood(g, ctx, lx, ly, 100, 12, 0); steel(g, ctx, lx - 46, ly - 30, 8, 58, 0); steel(g, ctx, lx + 46, ly - 30, 8, 58, 0);
  ctx.strokeStyle = '#7D879C'; ctx.lineWidth = 4; ctx.beginPath(); ctx.moveTo(lx - 44, ly - 60); ctx.lineTo(lx, ly - 90); ctx.lineTo(lx + 44, ly - 60); ctx.stroke();
  // bucket
  ctx.save(); ctx.translate(bx, by);
  ctx.strokeStyle = '#7D879C'; ctx.lineWidth = 4; ctx.beginPath(); ctx.moveTo(-50, -44); ctx.quadraticCurveTo(0, -100, 50, -44); ctx.stroke();
  ctx.restore();
  const sy = drop < 1 ? 20 + drop * drop * (by - 45) : by - 25;
  if (what === 'sack') drawSack(ctx, g, bx, sy, 0.9, 0, false);
  ctx.save(); ctx.translate(bx, by);
  ctx.beginPath(); ctx.moveTo(-52, -40); ctx.lineTo(52, -40); ctx.lineTo(42, 6); ctx.lineTo(-42, 6); ctx.closePath();
  ctx.fillStyle = g.lg(ctx, -40, 6, '#C3CAD8', '#7D879C'); ctx.fill(); ctx.lineWidth = 3; ctx.strokeStyle = INK; ctx.stroke();
  ctx.restore();
  if (t > 90 && t < 200) h.arrow(lx - 80, ly - 10, 0, 50, true);
  ctx.restore();
}
function introCart(ctx: Ctx2D, t: number, g: Gfx, h: IntroHelpers) {
  // the ball drops into the cart, the cart rolls down and tips over
  const x0 = 110, y0 = 150, x1 = 330, y1 = 200;
  const E: CartView = { e: { x: x0, y: y0, tx: x1, ty: y1, tip: true }, G: { w: 100 }, d: { x: 0, y: 0 }, sgn: 1, th: Math.atan2(y1 - y0, x1 - x0), tipK: 0, pose: { x: x0, y: y0, a: 0 } };
  const L = Math.hypot(x1 - x0, y1 - y0); E.d = { x: (x1 - x0) / L, y: (y1 - y0) / L };
  const k = t < 50 ? 0 : h.ease((t - 50) / 70);
  E.pose = { x: x0 + E.d.x * L * k, y: y0 + E.d.y * L * k, a: E.th }; E.rollS = L * k;
  E.tipK = t < 125 ? 0 : h.ease((t - 125) / 25);
  drawTrack(ctx, g, E); drawCartBack(ctx, g, E);
  // the ball
  let bx, by;
  if (t < 40) { bx = x0 + 6; by = 20 + (t / 40) * (t / 40) * 110; }
  else if (t < 135) { bx = E.pose.x + 6; by = E.pose.y - 20; }
  else { const q = (t - 135); bx = E.pose.x + 30 + q * 3; by = E.pose.y - 20 + q * q * 0.08; }
  if (by < 280) g.ball(ctx, h.fakeBall(bx, by, 3, 0, 15), 'hero', t);
  drawCart(ctx, g, E);
}

// ---------- Skins ----------
const skins: Record<string, SkinDef> = {
  // dirt shelf (tunnel floor)
  moles_dirt(ctx, b, g) {
    const { x, y } = b.position, w = b.dw!, h = b.dh!;
    g.block(ctx, x, y, w, h, b.angle, '#C9936A', '#8E5B3C', Math.min(10, h / 2));
    ctx.save(); ctx.translate(x, y); ctx.rotate(b.angle);
    ctx.fillStyle = 'rgba(80,45,25,.35)';
    for (let i = 0; i < Math.floor(w / 40); i++) { const px = -w / 2 + 20 + i * 40 + ((i * 17) % 13), py = ((i * 7) % 5) - 1; ctx.beginPath(); ctx.ellipse(px, py + h * 0.1, 4 + (i % 3), 3, 0, 0, 7); ctx.fill(); }
    ctx.fillStyle = '#7CC45E'; if (h < 60) { g.rr(ctx, -w / 2 + 3, -h / 2 - 1, w - 6, 5, 3); ctx.fillStyle = 'rgba(255,230,190,.55)'; ctx.fill(); }
    ctx.restore();
  },
  // wooden beam (posts, walls)
  moles_beam(ctx, b, g) {
    const { x, y } = b.position, w = b.dw!, h = b.dh!;
    g.block(ctx, x, y, w, h, b.angle, '#E6A968', '#A8683A', Math.min(8, w / 2, h / 2));
    ctx.save(); ctx.translate(x, y); ctx.rotate(b.angle);
    ctx.fillStyle = 'rgba(90,50,20,.55)';
    const vert = h > w;
    for (const k of [-1, 1]) { ctx.beginPath(); ctx.arc(vert ? 0 : k * (w / 2 - 10), vert ? k * (h / 2 - 10) : 0, 3, 0, 7); ctx.fill(); }
    ctx.restore();
  },
  // rock
  moles_rock(ctx, b, g) {
    const { x, y } = b.position, w = b.dw!, h = b.dh!;
    g.block(ctx, x, y, w, h, b.angle, '#C4BCD6', '#7F7798', Math.min(14, w / 2, h / 2));
    ctx.save(); ctx.translate(x, y); ctx.rotate(b.angle);
    ctx.strokeStyle = 'rgba(70,60,95,.35)'; ctx.lineWidth = 3; ctx.lineCap = 'round';
    ctx.beginPath(); ctx.moveTo(-w * 0.3, -h * 0.2); ctx.lineTo(-w * 0.1, h * 0.1); ctx.lineTo(w * 0.05, 0); ctx.moveTo(w * 0.2, h * 0.25); ctx.lineTo(w * 0.32, h * 0.05); ctx.stroke();
    ctx.restore();
  },
};

// ---------- Parts ----------
const parts = {
  // Heavy sack of soil (falls – counterweight)
  moles_sack: definePart({
    rects: [{ x: 0, y: 0, w: 46, h: 52 }], rot: false,
    draw(ctx, pl, sel, g) { drawSack(ctx, g, pl.x, pl.y, 1, 0, sel); },
    icon(ctx, g) { drawSack(ctx, g, 0, 0, 1.3, 0, false); },
    create(api, pl) {
      const b = api.add(api.R(pl.x, pl.y, 46, 50, { chamfer: { radius: 14 }, friction: 0.6, frictionStatic: 1, restitution: 0.05, frictionAir: 0.004 }));
      api.Body.setMass(b, MASS.sack); b.kind = 'moles_sack'; api.dynamic(b);
      api.onHit(b, o => { if (b.speed > 3 && !api.isBall(o)) api.sfx('moles_puff', b.position.x, b.position.y + 20, '#A8744F', 6); });
      return { layer: 'mid', draw(ctx, t, g) { drawSack(ctx, g, b.position.x, b.position.y, 1, b.angle, false); } };
    },
    intro(ctx, t, g, h) { introHoist(ctx, t, g, h, 'sack'); },
  }),
  // Crystal (half as heavy as the sack)
  moles_crystal: definePart({
    circle: 20, rot: false,
    draw(ctx, pl, sel, g) { drawGem(ctx, g, pl.x, pl.y, 0, sel); },
    icon(ctx, g) { drawGem(ctx, g, 0, 0, 0, false, 1.4); },
    create(api, pl) {
      const b = api.add(api.Bodies.polygon(pl.x, pl.y, 6, 20, { friction: 0.5, frictionStatic: 1, restitution: 0.1, angle: Math.PI / 6 }));
      api.Body.setMass(b, MASS.crystal); b.kind = 'moles_crystal'; api.dynamic(b);
      api.onHit(b, () => { if (b.speed > 3) api.sfx('moles_click', b.position.x, b.position.y, '#8FE8FF', 5); });
      return { layer: 'mid', draw(ctx, t, g) { drawGem(ctx, g, b.position.x, b.position.y, b.angle - Math.PI / 6, false); } };
    },
  }),
  // Leaf (almost weightless – drifts slowly down)
  moles_leaf: definePart({
    rects: [{ x: 0, y: 0, w: 52, h: 18 }], rot: false,
    draw(ctx, pl, sel, g) { drawLeaf(ctx, g, pl.x, pl.y, -0.15, sel); },
    icon(ctx, g) { ctx.scale(1.5, 1.5); drawLeaf(ctx, g, 0, 0, -0.35, false); },
    create(api, pl) {
      const b = api.add(api.R(pl.x, pl.y, 50, 14, { chamfer: { radius: 6 }, friction: 0.6, restitution: 0, frictionAir: 0.12, angle: -0.15 }));
      api.Body.setMass(b, MASS.leaf); b.kind = 'moles_leaf'; api.dynamic(b);
      const ph = pl.x * 0.1;
      return {
        layer: 'mid',
        step() { if (!b.isStatic && b.speed > 0.3 && api.touching(b).length === 0) api.Body.applyForce(b, b.position, { x: Math.sin(api.S.t * 0.06 + ph) * b.mass * 0.00035, y: 0 }); },
        draw(ctx, t, g) { drawLeaf(ctx, g, b.position.x, b.position.y, b.angle, false); },
      };
    },
  }),
};

// ---------- Objects ----------
const objs = {
  // Hoist: a rope over one (or two) wheels with an end on each side (lift, bucket, sack, gate, mine cart)
  moles_hoist: defineObj<HoistSt>({
    layer: 'back',
    create(api, o) {
      const sys = makeSystem(api, o as HoistObj);
      const st = { sys };
      api.thing({ layer: 'front', draw(ctx, t, g) { for (const E of sys.ends) drawEndFront(ctx, g, E); } });
      return st;
    },
    step(api, st) { stepSystem(api, st.sys); for (const E of st.sys.ends) if (E.type === 'cart') E.rollS = st.sys.s; },
    busy(api, st) { return busySystem(st.sys); },
    blockers(od) {
      const o = od as HoistObj;
      const out: Blocker[] = [];
      for (const w of o.wheels || (o.wx != null ? [{ x: o.wx, y: o.wy! }] : [])) out.push({ x: w.x, y: w.y, r: 38 });
      for (const e of [o.A, o.B]) if (e) {
        if (e.type === 'lift') out.push({ x: e.x, y: e.y - 34, r: 58 });
        if (e.type === 'cart') out.push({ x: e.x, y: e.y - 16, r: 52 });
        if (e.type === 'gate') for (let k = -1; k <= 1; k++) out.push({ x: e.x, y: e.y + k * (e.h || 150) / 3, r: 26 });
        if (e.type === 'sack') out.push({ x: e.x, y: e.y - 30, r: 36 });
        if (e.type === 'bucket') for (const k of [-1, 1]) out.push({ x: e.x + k * (e.w || 100) / 2, y: e.y - 20, r: 16 });
      }
      return out;
    },
    draw(ctx, st, t, g) {
      const sys = st.sys;
      for (const E of sys.ends) drawEndBack(ctx, g, E);
      drawRope(ctx, sys);
      for (const w of sys.wheels) drawWheel(ctx, g, w, sys.spin, sys);
    },
    intro(ctx, t, g, h) { introHoist(ctx, t, g, h, 'sack'); },
  }),
  // Mine cart on rails (rolls down the track once the ball is in, and tips over at the end)
  moles_cart: defineObj<HoistSt>({
    layer: 'back',
    create(api, od) {
      const o = od as CartObj;
      const sys = makeSystem(api, { A: Object.assign({ type: 'cart' as const }, o), max: Math.hypot(o.tx - o.x, o.ty - o.y), hold: o.hold !== false, damp: 0.995, vmax: 7 });
      const st = { sys };
      api.thing({ layer: 'front', draw(ctx, t, g) { drawCart(ctx, g, sys.ends[0]); } });
      return st;
    },
    step(api, st) { stepSystem(api, st.sys); st.sys.ends[0].rollS = st.sys.s; },
    busy(api, st) { return busySystem(st.sys); },
    blockers(o) { return [{ x: o.x, y: o.y - 16, r: 52 }]; },
    draw(ctx, st, t, g) { drawTrack(ctx, g, st.sys.ends[0]); drawCartBack(ctx, g, st.sys.ends[0]); },
    intro(ctx, t, g, h) { introCart(ctx, t, g, h); },
  }),
  // Decoration: a mole in a helmet, watching
  moles_mole: defineObj({
    layer: 'back',
    create() { return {}; },
    draw(ctx, st, t, g) { const o = st.o as ObjData & { s?: number; look?: number }; mole(ctx, g, o.x, o.y, o.s || 1, t + o.x, o.look || 1); },
  }),
};

// ---------- Sounds ----------
const sounds: Record<string, SoundFn> = {
  moles_clonk: (tone) => { tone(520, 0.12, 'square', 0.12, 380); tone(780, 0.18, 'triangle', 0.18, 700, 0.08); },
  moles_bump: (tone, noise) => { noise(0.18, 0.3, 300, 1); tone(110, 0.2, 'sine', 0.35, 60); },
  moles_tip: (tone, noise) => { tone(300, 0.25, 'triangle', 0.25, 160); noise(0.3, 0.15, 900, 2, 0.1); },
  moles_click: (tone) => { tone(1200, 0.05, 'square', 0.12, 900); tone(1500, 0.05, 'square', 0.1, 1100, 0.06); },
  moles_puff: (tone, noise) => { noise(0.25, 0.25, 250, 0.8); },
};

// ---------- Reskins (kit parts, ball and goal in the world's style) ----------
const reskin: WorldDef['reskin'] = {
  // plank = a piece of rail track (wooden sleeper with an iron rail)
  plank(ctx, pl, sel, g, rects) { const r = rects[0]; railPlank(ctx, g, r.x, r.y, r.w, r.h, r.a, sel); },
  long_plank(ctx, pl, sel, g, rects) { const r = rects[0]; railPlank(ctx, g, r.x, r.y, r.w, r.h, r.a, sel); },
  // pad = a springy mushroom
  pad(ctx, pl, sel, g, rects) { const r = rects[0]; mushroom(ctx, g, r.x, r.y, r.a, r.w, sel); },
  // stone = a lump of coal
  stone(ctx, pl, sel, g) { coal(ctx, g, pl.x, pl.y, (pl.a || 0) * DEG, sel); },
  // paint = mud puddle
  paint(ctx, pl, sel, g) { mud(ctx, g, pl.x, pl.y, sel); },
};
const icons: WorldDef['icons'] = {
  plank(ctx, g) { railPlank(ctx, g, 0, 0, 100, 16, -0.35); },
  long_plank(ctx, g) { railPlank(ctx, g, 0, 0, 110, 14, -0.35); },
  pad(ctx, g) { mushroom(ctx, g, 0, -8, 0, 90); },
  stone(ctx, g) { coal(ctx, g, 0, 0, 0); },
  paint(ctx, g) { mud(ctx, g, 0, 0); },
};

// The hero: a shiny crystal ball with a face and a small miner's helmet
function ball(ctx: Ctx2D, b: GameBody, kind: string, t: number, g: Gfx) {
  const { x, y } = b.position, r = b.circleRadius!, v = b.velocity || { x: 0, y: 0 };
  ctx.save(); ctx.translate(x, y);
  g.shadowOn(ctx, 8, 5);
  const gr = ctx.createRadialGradient(-r * .35, -r * .4, r * .1, 0, 0, r * 1.05);
  if (kind === 'hero') { gr.addColorStop(0, '#FFE4F6'); gr.addColorStop(0.45, '#FF8FD0'); gr.addColorStop(1, '#C23C9A'); }
  else { gr.addColorStop(0, '#E0F8FF'); gr.addColorStop(0.5, '#6FD3F0'); gr.addColorStop(1, '#2B7FC9'); }
  ctx.beginPath(); ctx.arc(0, 0, r, 0, 7); ctx.fillStyle = gr; ctx.fill(); g.shadowOff(ctx);
  ctx.lineWidth = 2; ctx.strokeStyle = 'rgba(59,42,92,.8)'; ctx.stroke();
  // facets that roll
  ctx.save(); ctx.rotate(b.roll || 0);
  ctx.strokeStyle = 'rgba(255,255,255,.45)'; ctx.lineWidth = 1.5;
  ctx.beginPath(); for (let i = 0; i < 6; i++) { const a = i * Math.PI / 3; ctx.moveTo(Math.cos(a) * r * 0.55, Math.sin(a) * r * 0.55); ctx.lineTo(Math.cos(a) * r * 0.95, Math.sin(a) * r * 0.95); } ctx.stroke();
  ctx.restore();
  // face (stays upright)
  const lx = Math.max(-2, Math.min(2, v.x * 0.4)), ly = Math.max(-2, Math.min(2, v.y * 0.4));
  const scared = v.y > 9;
  for (const s of [-1, 1]) {
    ctx.beginPath(); ctx.arc(s * r * 0.34 + lx * 0.4, -r * 0.05, r * 0.26, 0, 7); ctx.fillStyle = '#fff'; ctx.fill();
    ctx.beginPath(); ctx.arc(s * r * 0.34 + lx * 0.8, -r * 0.05 + ly * 0.6, r * (scared ? 0.1 : 0.14), 0, 7); ctx.fillStyle = INK; ctx.fill();
  }
  ctx.strokeStyle = INK; ctx.fillStyle = INK; ctx.lineWidth = 2; ctx.lineCap = 'round';
  if (b.sad) { ctx.beginPath(); ctx.arc(lx * 0.4, r * 0.6, r * 0.24, 1.2 * Math.PI, 1.8 * Math.PI); ctx.stroke(); }
  else if (scared || Math.hypot(v.x, v.y) > 8.5) { ctx.beginPath(); ctx.ellipse(lx * 0.4, r * 0.42, r * 0.15, r * 0.2, 0, 0, 7); ctx.fill(); }
  else { ctx.beginPath(); ctx.arc(lx * 0.4, r * 0.28, r * 0.26, 0.15 * Math.PI, 0.85 * Math.PI); ctx.stroke(); }
  ctx.fillStyle = 'rgba(255,90,140,.5)';
  for (const s of [-1, 1]) { ctx.beginPath(); ctx.ellipse(s * r * 0.62, r * 0.25, r * 0.14, r * 0.09, 0, 0, 7); ctx.fill(); }
  // miner's helmet with lamp
  if (kind === 'hero') {
    ctx.beginPath(); ctx.arc(0, -r * 0.42, r * 0.78, Math.PI * 1.05, Math.PI * 1.95); ctx.closePath();
    ctx.fillStyle = '#FFC93C'; ctx.fill(); ctx.lineWidth = 1.8; ctx.strokeStyle = INK; ctx.stroke();
    ctx.fillStyle = '#FFF7C2'; ctx.beginPath(); ctx.arc(0, -r * 1.02, r * 0.22, 0, 7); ctx.fill(); ctx.stroke();
    ctx.fillStyle = 'rgba(255,245,170,.35)'; ctx.beginPath(); ctx.arc(0, -r * 1.02, r * 0.5 + Math.sin(t * 0.15) * 1.5, 0, 7); ctx.fill();
  }
  ctx.beginPath(); ctx.ellipse(-r * .42, -r * .02, r * .12, r * .08, -0.6, 0, 7); ctx.fillStyle = 'rgba(255,255,255,.7)'; ctx.fill();
  ctx.restore();
}

// The goal: a treasure chest
interface GoalPos { x: number; y: number; w: number }
const goal = {
  bowl(ctx: Ctx2D, gl: GoalPos, full: number, t: number, g: Gfx) {
    const { x, y, w } = gl;
    ctx.save();
    // open lid (behind)
    g.shadowOn(ctx, 12, 6);
    ctx.beginPath(); ctx.moveTo(x - w / 2 - 2, y - 56); ctx.lineTo(x - w / 2 + 8, y - 112); ctx.quadraticCurveTo(x, y - 136, x + w / 2 - 8, y - 112); ctx.lineTo(x + w / 2 + 2, y - 56); ctx.closePath();
    ctx.fillStyle = g.lg(ctx, y - 130, y - 56, '#C0703A', '#8A4A22'); ctx.fill(); g.shadowOff(ctx);
    ctx.lineWidth = 3; ctx.strokeStyle = INK; ctx.stroke();
    ctx.strokeStyle = '#FFC93C'; ctx.lineWidth = 6;
    for (const k of [-1, 1]) { ctx.beginPath(); ctx.moveTo(x + k * w / 3.2, y - 58); ctx.lineTo(x + k * w / 3.6, y - 118); ctx.stroke(); }
    // inside
    g.rr(ctx, x - w / 2, y - 62, w, 30, 8); ctx.fillStyle = '#5A2E18'; ctx.fill();
    // gold
    const n = full ? 9 : 5;
    for (let i = 0; i < n; i++) {
      const cx = x - w / 2 + 18 + (i / (n - 1)) * (w - 36), cy = y - 58 + (i % 2) * 5 - (full ? 6 : 0);
      ctx.beginPath(); ctx.ellipse(cx, cy, 10, 6, 0, 0, 7); ctx.fillStyle = '#FFD84D'; ctx.fill(); ctx.lineWidth = 2; ctx.strokeStyle = '#C98A10'; ctx.stroke();
    }
    // sparkles
    const sp = full ? 1 : 0.5;
    for (let i = 0; i < 3; i++) {
      const k = ((t * 0.02 * sp + i / 3) % 1), sx = x - w / 3 + i * w / 3, sy = y - 70 - k * 30;
      ctx.globalAlpha = 1 - k; ctx.fillStyle = '#FFF7C2';
      ctx.beginPath(); ctx.moveTo(sx, sy - 6); ctx.lineTo(sx + 2, sy - 2); ctx.lineTo(sx + 6, sy); ctx.lineTo(sx + 2, sy + 2); ctx.lineTo(sx, sy + 6); ctx.lineTo(sx - 2, sy + 2); ctx.lineTo(sx - 6, sy); ctx.lineTo(sx - 2, sy - 2); ctx.closePath(); ctx.fill();
    }
    ctx.restore();
  },
  front(ctx: Ctx2D, gl: GoalPos, g: Gfx) {
    const { x, y, w } = gl;
    ctx.save();
    g.shadowOn(ctx, 10, 5);
    g.rr(ctx, x - w / 2 - 4, y - 50, w + 8, 58, 10); ctx.fillStyle = g.lg(ctx, y - 50, y + 8, '#D98A4E', '#9A5428'); ctx.fill(); g.shadowOff(ctx);
    ctx.lineWidth = 3; ctx.strokeStyle = INK; ctx.stroke();
    // planks
    ctx.strokeStyle = 'rgba(90,45,20,.45)'; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.moveTo(x - w / 2, y - 30); ctx.lineTo(x + w / 2, y - 30); ctx.moveTo(x - w / 2, y - 12); ctx.lineTo(x + w / 2, y - 12); ctx.stroke();
    // gold bands
    ctx.fillStyle = '#FFC93C';
    for (const k of [-1, 1]) { g.rr(ctx, x + k * w / 3.2 - 6, y - 50, 12, 58, 3); ctx.fill(); ctx.lineWidth = 2; ctx.strokeStyle = INK; ctx.stroke(); }
    g.rr(ctx, x - w / 2 - 4, y - 54, w + 8, 10, 5); ctx.fill(); ctx.stroke();
    // lock
    g.rr(ctx, x - 12, y - 46, 24, 26, 6); ctx.fillStyle = '#FFE07A'; ctx.fill(); ctx.stroke();
    ctx.fillStyle = INK; ctx.beginPath(); ctx.arc(x, y - 36, 3.5, 0, 7); ctx.fill(); ctx.fillRect(x - 1.5, y - 36, 3, 9);
    ctx.restore();
  },
  bell(ctx: Ctx2D, gl: { x: number; y: number; hitT?: number }, t: number, g: Gfx) {
    // miner's bell under a beam
    const sw = gl.hitT != null ? Math.sin((t - gl.hitT) * 0.35) * Math.exp(-(t - gl.hitT) * 0.03) * 0.6 : Math.sin(t * 0.03) * 0.04;
    ctx.save(); ctx.translate(gl.x, gl.y - 44);
    g.block(ctx, 0, -8, 70, 12, 0, '#E6A968', '#A8683A', 5);
    ctx.rotate(sw);
    ctx.strokeStyle = INK; ctx.lineWidth = 3; ctx.beginPath(); ctx.moveTo(0, -2); ctx.lineTo(0, 10); ctx.stroke();
    g.shadowOn(ctx, 12, 6);
    ctx.beginPath(); ctx.moveTo(-8, 10); ctx.quadraticCurveTo(-26, 16, -30, 58); ctx.lineTo(30, 58); ctx.quadraticCurveTo(26, 16, 8, 10); ctx.closePath();
    ctx.fillStyle = g.lg(ctx, 10, 58, '#FFE07A', '#D98A1A'); ctx.fill(); g.shadowOff(ctx);
    ctx.lineWidth = 3; ctx.stroke();
    ctx.beginPath(); ctx.ellipse(-10, 30, 4, 12, 0.3, 0, 7); ctx.fillStyle = 'rgba(255,255,255,.6)'; ctx.fill();
    ctx.beginPath(); ctx.arc(0, 62, 7, 0, 7); ctx.fillStyle = '#8A4A22'; ctx.fill();
    ctx.restore();
  },
};

// ---------- Meta ----------
const meta: WorldMeta = {
  icon: '🐹', strings, music: 'moles.mp3',
  credits: { title: 'Fantasy Worlds · Crystal Caverns', artist: 'Vadim_Makes_Sound', id: 569995, url: 'https://pixabay.com/music/fantasy-dreamy-childrens-fantasy-worlds-crystal-caverns-569995/' },
  synth: { bpm: 64, root: 329.6, sc: 'minor', prog: [0, 5, 2, 6], bar: 4, lead: 'bell', dens: 0.3 },
};

const world = defineWorld({
  k: K, meta, scene, wash: 'rgba(255,238,214,.58)', bgExtra, skins, parts, objs, sounds, reskin, icons, ball, goal, levels,
});
export default world;
